import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SessionService } from 'src/app/shared/services/session.service';
import { MembershipService } from '../../../services/membership.service';
import { MatDialogRef } from '@angular/material/dialog';
import { MembershipPlanList } from '../../../models/membership-plan-list';
import { BaseModalComponent } from 'src/app/core/base-modal.componant';
import { MembershipCustomerListQueryFilter } from '../../../memberships/membership-customer-list/membership-customer-list-query-filter';
import { StripeCardElementOptions, StripeElementsOptions } from '@stripe/stripe-js';
import { injectStripe, StripeCardComponent, StripeService } from 'ngx-stripe';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'membership-customer-create-modal',
  templateUrl: './membership-customer-create-modal.component.html',
  styleUrl: './membership-customer-create-modal.component.scss',
  standalone: false,
})
export class MembershipCustomerCreateModalComponent extends BaseModalComponent implements OnInit {
  readonly stripe = injectStripe();

  membershipCustomerForm: FormGroup;
  membershipPlans: MembershipPlanList[] = [];
  filter: MembershipCustomerListQueryFilter = new MembershipCustomerListQueryFilter();
  isLoading = false;

  private selectedMembershipPlan: MembershipPlanList;

  @ViewChild(StripeCardComponent) cardElement!: StripeCardComponent;

  public cardOptions: StripeCardElementOptions = {
    style: {
      base: {
        fontFamily: 'Poppins, sans-serif',
        fontSize: '18px',
      },
    },
  };

  public elementsOptions: StripeElementsOptions = {
    appearance: {
      theme: 'stripe'
    },
    locale: 'en',
  };


  constructor(
    private fb: FormBuilder,
    private sessionService: SessionService,
    private membershipService: MembershipService,
    private stripeService: StripeService,
    public dialogRef: MatDialogRef<MembershipCustomerCreateModalComponent>
  ) {
    super();
  }

  ngOnInit(): void {
    this.filter.organizationId = this.sessionService.getUser()?.organizationId || null;
    this.membershipCustomerForm = this.fb.group({
      membershipPlanId: ['', Validators.required],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      emailAddress: ['', [Validators.required, Validators.email]],
      startDate: [''],
      endDate: ['']
    });
    this.stripeService.changeKey(environment.stripe.token, {
      stripeAccount: this.sessionService.getUser().stripeAccountId
    });
    this.loadMembershipPlans();
  }

  loadMembershipPlans() {
    this.isLoading = true;
    this.membershipService.getAllPlansByOrganization(this.filter).subscribe({
      next: (result: any) => {
        this.membershipPlans = result.results || result;
        this.isLoading = false;
      },
      error: (error: any) => {
        this.isLoading = false;
        console.error(error);
      }
    });
  }

  setMembershipPlan(plan: MembershipPlanList) {
    this.selectedMembershipPlan = plan;
    this.setStartAndEndDate();
  }

  onSubmit() {
    if (this.membershipCustomerForm.valid && this.filter.organizationId) {
      this.isLoading = true;

      this.stripe.createPaymentMethod({
        type: 'card',
        card: this.cardElement.element,
        billing_details: {
          name: `${this.membershipCustomerForm.value.firstName} ${this.membershipCustomerForm.value.lastName}`,
          email: this.membershipCustomerForm.value.emailAddress
        }
      }).subscribe({
        next: (paymentMethodResponse) => {
          this.onCreatePaymentMethodSuccess(paymentMethodResponse);
        },
        error: (err: any) => {
          console.log(err);
          this.isLoading = false;
        }
      });
    }
  }

  private onCreatePaymentMethodSuccess(paymentMethodResponse: any) {
    const formValue = {
      ...this.membershipCustomerForm.value,
      organizationId: this.filter.organizationId,
      createdBy: this.sessionService.getUserId(),
      membershipPlanId: this.selectedMembershipPlan.id,
      paymentMethodId: paymentMethodResponse.paymentMethod.id
    };
    formValue.createdBy = this.sessionService.getUserId();
    this.membershipService.createCustomer(formValue).subscribe({
      next: (result) => {
        this.isLoading = false;
        this.dialogRef.close("save");
      },
      error: (error) => {
        console.error('Error creating membership customer:', error);
        this.isLoading = false;
      }
    });
  }

  cancel() {
    this.dialogRef.close('cancel');
  }

  private setStartAndEndDate() {
    const startDate = new Date();
    const endDate = new Date();

    if (this.selectedMembershipPlan.billingInterval === 'week') {
      endDate.setDate(startDate.getDate() + 7);
    } else if (this.selectedMembershipPlan.billingInterval === 'month') {
      endDate.setMonth(startDate.getMonth() + 1);
    } else if (this.selectedMembershipPlan.billingInterval === 'year') {
      endDate.setFullYear(startDate.getFullYear() + 1);
    }

    this.membershipCustomerForm.patchValue({
      startDate: this.convertToUTC(startDate),
      endDate: this.convertToUTC(endDate)
    });
  }

  private convertToUTC(date: Date): Date {
    return new Date(Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes()
    ));
  }
}
